import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../services/api';
import { salvarEmpresa, empresaAtiva, EmpresaAtiva } from '../utils/empresa';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

/** Empresa retornada no login (vínculo ativo do usuário) */
export interface EmpresaLogin {
  id: string;
  slug: string;
  nome: string;
  perfil: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  empresas: EmpresaLogin[];
  /** Restaurante ativo (localStorage 'empresa') — reativo para o layout */
  empresa: EmpresaAtiva | null;
  /** Perfil efetivo dentro do restaurante: perfil do vínculo; ADMIN global = SUPER_ADMIN */
  perfilRestaurante: string | null;
  /** Operador da plataforma (role global ADMIN) — vê a área /plataforma */
  ehOperador: boolean;
  login: (email: string, password: string) => Promise<EmpresaLogin[]>;
  selecionarEmpresa: (tenantId: string) => Promise<void>;
  entrarComoVisitante: (tenantId: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [empresas, setEmpresas] = useState<EmpresaLogin[]>([]);
  const [empresa, setEmpresa] = useState<EmpresaAtiva | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      setEmpresa(empresaAtiva());
    }

    setIsLoading(false);
  }, []);

  /** Recarrega os restaurantes do usuário (login com múltiplos / troca). */
  const recarregarEmpresas = useCallback(async () => {
    try {
      const response = await api.get('/auth/empresas');
      setEmpresas(response.data.data ?? []);
    } catch {
      setEmpresas([]);
    }
  }, []);

  /** Página carregada logada (F5): empresas voltam para permitir trocar. */
  useEffect(() => {
    if (token) {
      recarregarEmpresas();
    }
  }, [token, recarregarEmpresas]);

  /** Salva o token já com tenant (resultado de selecionar-empresa / entrar). */
  const aplicarTokenTenant = (novoToken: string, novaEmpresa: EmpresaAtiva) => {
    setToken(novoToken);
    localStorage.setItem('token', novoToken);
    salvarEmpresa(novaEmpresa);
    setEmpresa(novaEmpresa);
  };

  const login = async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    const { token: newToken, user: newUser, empresas: novasEmpresas } = response.data.data;

    // Sem NENHUM restaurante ativo vinculado, só o operador da plataforma
    // (role ADMIN) tem o que fazer. Não-ADMIN não ganha token: ficaria
    // "autenticado" sem acesso e o redirect do /login o jogaria no painel
    // errado (vazamento de tenant pelo fallback do backend).
    if ((novasEmpresas ?? []).length === 0 && newUser.role !== 'ADMIN') {
      throw new Error(
        'Sua conta não está vinculada a nenhum restaurante ativo. Peça ao administrador.'
      );
    }

    setUser(newUser);
    setEmpresas(novasEmpresas ?? []);
    localStorage.setItem('user', JSON.stringify(newUser));

    // Com UM restaurante, já seleciona (token ganha tenant + perfil).
    // Salva o token base ANTES: o interceptor do axios lê o localStorage a
    // cada request — sem ele, o selecionar-empresa vai sem Authorization (401).
    if (novasEmpresas && novasEmpresas.length === 1) {
      setToken(newToken);
      localStorage.setItem('token', newToken);
      const e = novasEmpresas[0];
      const sel = await api.post('/auth/selecionar-empresa', { tenantId: e.id });
      aplicarTokenTenant(sel.data.data.token, sel.data.data.empresa);
    } else {
      // Vários restaurantes (ou nenhum): token base + espera a seleção explícita
      setToken(newToken);
      localStorage.setItem('token', newToken);
      salvarEmpresa(null);
      setEmpresa(null);
    }

    return novasEmpresas ?? [];
  };

  const selecionarEmpresa = async (tenantId: string) => {
    const response = await api.post('/auth/selecionar-empresa', { tenantId });
    const { token: novoToken, empresa: novaEmpresa } = response.data.data;
    aplicarTokenTenant(novoToken, novaEmpresa);
  };

  /** Operador da plataforma entra num restaurante com poder emprestado (sem vínculo). */
  const entrarComoVisitante = async (tenantId: string) => {
    const response = await api.post(`/tenants/${tenantId}/entrar`);
    const { token: novoToken, empresa: novaEmpresa } = response.data.data;
    aplicarTokenTenant(novoToken, novaEmpresa);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setEmpresas([]);
    setEmpresa(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    salvarEmpresa(null);
  };

  // Papel efetivo DENTRO do restaurante ativo: o perfil do vínculo manda;
  // o operador global (ADMIN) também atua como SUPER_ADMIN (equivalência do backend).
  const perfilRestaurante = empresa?.perfil ?? (user?.role === 'ADMIN' ? 'SUPER_ADMIN' : null);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        empresas,
        empresa,
        perfilRestaurante,
        ehOperador: user?.role === 'ADMIN',
        login,
        selecionarEmpresa,
        entrarComoVisitante,
        logout,
        isAuthenticated: !!token,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
}
